"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useCallback } from "react";
import { Loader2, Clock, CheckCircle, Building2, Receipt } from "lucide-react";
import { formatCurrency } from "@/lib/formatting";
import { getSellerStepForStatus, getBuyerStepForStatus } from "@/lib/deals/types";
import type { DealWithRelations } from "@/lib/deals/types";
import type { AnonymizedDealRelations } from "@/lib/platform";
import type { DealVerification } from "@/lib/deals/verification/types";
import type { DealContractReview } from "@/lib/deals/contract-review/types";
import type { DealSignatures } from "@/lib/deals/signature/types";
import type { DealPayments } from "@/lib/deals/payment/types";
import { VerificationStatus, OkokontoUpload, LegalEntityAddressForm } from "@/components/deals/verification";
import { ContractReviewStep, ContractDraftReview } from "@/components/deals/contract";
import { SigningStep } from "@/components/deals/signature";
import { UnbWaitingStatus } from "@/components/deals/unb";
import { PaymentSummary, PaymentChecklist } from "@/components/deals/payment";
import { CompletionStep } from "@/components/deals/completion";
import { getStepHeader, type StepHeaderConfig } from "@/components/deals/step-header";
import { dealTotal, tradedPositions } from "@/lib/deals/positions";
import { positionPrice, positionQuantity, positionTotal } from "@/lib/deals/position-labels";
type AnonymizedDeal = Omit<DealWithRelations, "seller" | "buyer"> & AnonymizedDealRelations;
interface DealStepContentProps {
  dealId: string;
  deal: AnonymizedDeal;
  userRole: "seller" | "buyer";
  userId: string;
  onRefresh?: () => void;
}
function StepHeader(t0) {
  const $ = _c(16);
  const {
    icon: Icon,
    iconBg,
    iconColor,
    headline,
    description
  } = t0;
  const t1 = `w-12 h-12 rounded-full ${iconBg} flex items-center justify-center flex-shrink-0`;
  const t2 = `w-6 h-6 ${iconColor}`;
  let t3;
  if ($[0] !== Icon || $[1] !== t2) {
    t3 = <Icon className={t2} />;
    $[0] = Icon;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[3] = t1;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== headline) {
    t5 = <h3 className="font-semibold text-primary">{headline}</h3>;
    $[6] = headline;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== description) {
    t6 = <p className="text-sm text-on-surface-variant mt-1">{description}</p>;
    $[8] = description;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <div>{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t7) {
    t8 = <div className="flex items-start gap-4">{t4}{t7}</div>;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

/**
 * DealStepContent - Renders the actionable content for the current deal step.
 *
 * Determines the current step from deal.status + userRole, fetches
 * step-specific data from API routes, and renders the appropriate
 * step component.
 */
export function DealStepContent({
  dealId,
  deal,
  userRole,
  userId: _userId,
  onRefresh
}: DealStepContentProps) {
  const currentStep = userRole === "seller" ? getSellerStepForStatus(deal.status) : getBuyerStepForStatus(deal.status);
  const [verification, setVerification] = useState<DealVerification | null>(null);
  const [contractReview, setContractReview] = useState<DealContractReview | null>(null);
  const [signatures, setSignatures] = useState<DealSignatures | null>(null);
  const [payment, setPayment] = useState<DealPayments | null>(null);
  const [buyerDocuments, setBuyerDocuments] = useState(deal.buyer_documents ?? []);
  const [userProfile, setUserProfile] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const fetchStepData = async () => {
      setLoading(true);
      try {
        switch (currentStep) {
          case "identity_verification":
          case "eco_account_verification":
            {
              const [verRes, crRes_0, profRes] = await Promise.all([fetch(`/api/deals/${dealId}/verification`), fetch(`/api/deals/${dealId}/contract-review`), fetch(`/api/user/profile`)]);
              if (!cancelled) {
                if (verRes.ok) {
                  const d_4 = await verRes.json();
                  setVerification(d_4.data);
                }
                if (crRes_0.ok) {
                  const d_5 = await crRes_0.json();
                  setContractReview(d_5.data);
                }
                if (profRes.ok) {
                  const d_6 = await profRes.json();
                  setUserProfile(d_6.profile);
                }
              }
              break;
            }
          case "contract_preparation":
            {
              // Only fetch when status is contract_review (needs contract data for review UI)
              if (deal.status === "contract_review") {
                const crRes = await fetch(`/api/deals/${dealId}/contract-review`);
                if (crRes.ok && !cancelled) {
                  const d_3 = await crRes.json();
                  setContractReview(d_3.data);
                }
              }
              break;
            }
          case "contract":
            {
              const [crRes2, sigRes] = await Promise.all([fetch(`/api/deals/${dealId}/contract-review`), fetch(`/api/deals/${dealId}/signature`)]);
              if (!cancelled) {
                if (crRes2.ok) {
                  const d_1 = await crRes2.json();
                  setContractReview(d_1.data);
                }
                if (sigRes.ok) {
                  const d_2 = await sigRes.json();
                  setSignatures(d_2.data);
                }
              }

              // If user just returned from DocuSign signing, sync status from DocuSign API
              if (!cancelled && typeof window !== "undefined") {
                const params = new URLSearchParams(window.location.search);
                if (params.get("event") === "signing_complete") {
                  try {
                    const syncRes = await fetch(`/api/deals/${dealId}/signature/sync`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json"
                      },
                      body: JSON.stringify({
                        completed: true
                      })
                    });
                    if (syncRes.ok && !cancelled) {
                      const syncData = await syncRes.json();
                      setSignatures(syncData.data);
                    }
                  } catch (err) {
                    console.error("[DealStepContent] Signature sync failed:", err);
                  }
                  // Clean up the URL query param
                  if (!cancelled) {
                    const url = new URL(window.location.href);
                    url.searchParams.delete("event");
                    window.history.replaceState({}, "", url.toString());
                  }
                }
              }
              break;
            }
          case "payment_pending":
            {
              const [payRes, dealRes] = await Promise.all([fetch(`/api/deals/${dealId}/payment`), fetch(`/api/deals/${dealId}`)]);
              if (!cancelled) {
                if (payRes.ok) {
                  const d = await payRes.json();
                  setPayment(d.data);
                }
                if (dealRes.ok) {
                  const d_0 = await dealRes.json();
                  setBuyerDocuments(d_0.data?.buyer_documents ?? []);
                }
              }
              break;
            }
          // unb_confirmation, completed: no fetch needed
        }
      } catch (error) {
        console.error(`[DealStepContent] Failed to fetch ${currentStep} data:`, error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStepData();
    return () => {
      cancelled = true;
    };
  }, [dealId, currentStep, deal.status]);

  // Poll for signature status updates when on the contract step
  // (handles the case where signing happens in a new tab via DocuSign)
  useEffect(() => {
    if (currentStep !== "contract") return;
    const myStatusKey = userRole === "seller" ? "seller_signature_status" : "buyer_signature_status";
    const myStatus = signatures?.[myStatusKey];

    // Only poll if the user hasn't signed yet
    if (!myStatus || myStatus === "signed") return;
    const interval = setInterval(async () => {
      try {
        // Poll the DB for status updates (webhook or sync may have updated it)
        const res = await fetch(`/api/deals/${dealId}/signature`);
        if (res.ok) {
          const data = await res.json();
          const updated = data.data;
          if (updated?.[myStatusKey] === "signed") {
            setSignatures(updated);
            onRefresh?.();
          }
        }
      } catch {
        // Silently ignore polling errors
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [dealId, currentStep, userRole, signatures, onRefresh]);

  // Callbacks for contract review
  const handleUpdateBilling = useCallback(async (values: {
    company?: string;
    name: string;
    addressLine1: string;
    addressLine2?: string;
    postalCode: string;
    city: string;
    country: string;
    taxId?: string;
    phone?: string;
    iban?: string;
    signerName?: string;
    signerEmail?: string;
  }) => {
    const prefix = userRole === "seller" ? "seller" : "buyer";
    const payload: Record<string, unknown> = {
      [`${prefix}_billing_company`]: values.company,
      [`${prefix}_billing_name`]: values.name,
      [`${prefix}_billing_address_line1`]: values.addressLine1,
      [`${prefix}_billing_address_line2`]: values.addressLine2,
      [`${prefix}_billing_postal_code`]: values.postalCode,
      [`${prefix}_billing_city`]: values.city,
      [`${prefix}_billing_country`]: values.country,
      [`${prefix}_billing_tax_id`]: values.taxId,
      [`${prefix}_billing_phone`]: values.phone,
      [`${prefix}_signer_name`]: values.signerName,
      [`${prefix}_signer_email`]: values.signerEmail
    };
    // IBAN is seller-only
    if (prefix === "seller" && values.iban) {
      payload.seller_iban = values.iban;
    }
    const res_0 = await fetch(`/api/deals/${dealId}/contract-review`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });
    if (res_0.ok) {
      const data_0 = await res_0.json();
      setContractReview(data_0.data);
      onRefresh?.();
    }
  }, [dealId, userRole, onRefresh]);

  // Callback for signing
  const handleSign = useCallback(async (): Promise<string | void> => {
    const res_1 = await fetch(`/api/deals/${dealId}/signature`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        return_url: `${window.location.origin}${window.location.pathname}`
      })
    });
    if (res_1.ok) {
      const data_1 = await res_1.json();
      const signingUrl = data_1.data?.signing_url;
      if (signingUrl) {
        return signingUrl;
      }
    }
  }, [dealId]);

  // Callback for verification upload
  const handleVerificationUpload = useCallback((updatedVerification: DealVerification) => {
    setVerification(updatedVerification);
    onRefresh?.();
  }, [onRefresh]);

  // Show loading only for steps that need data
  const needsFetch = ["identity_verification", "eco_account_verification", "contract", "payment_pending"].includes(currentStep) || currentStep === "contract_preparation" && deal.status === "contract_review";
  if (needsFetch && loading) {
    return <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-center gap-2 text-on-surface-variant py-4">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Lade Schrittdetails...</span>
        </div>
      </div>;
  }

  // Build address initialValues by merging user profile (fallback) with deal contract-review data (priority)
  const getAddressInitialValues = (prefix_0: "seller" | "buyer") => {
    const prof = userProfile;
    const cr = contractReview;
    const crVal = (field: string) => cr?.[`${prefix_0}_billing_${field}` as keyof DealContractReview] as string | null | undefined;
    const profileName = prof ? [prof.first_name, prof.last_name].filter(Boolean).join(" ") || null : null;
    const signerName = cr?.[`${prefix_0}_signer_name` as keyof DealContractReview] as string | null | undefined;
    const signerEmail = cr?.[`${prefix_0}_signer_email` as keyof DealContractReview] as string | null | undefined;
    return {
      company: crVal("company") || prof?.company_name as string | null || null,
      name: crVal("name") || profileName,
      addressLine1: crVal("address_line1") || prof?.address_line1 as string | null || null,
      addressLine2: crVal("address_line2") || prof?.address_line2 as string | null || null,
      postalCode: crVal("postal_code") || prof?.postal_code as string | null || null,
      city: crVal("city") || prof?.city as string | null || null,
      country: crVal("country") || prof?.country as string | null || "DE",
      taxId: crVal("tax_id") || prof?.tax_id as string | null || null,
      phone: crVal("phone") || prof?.phone as string | null || null,
      iban: prefix_0 === "seller" ? cr?.seller_iban as string | null || null : null,
      signerName: signerName || profileName,
      signerEmail: signerEmail || prof?.email as string | null || null
    };
  };
  switch (currentStep) {
    case "identity_verification":
      {
        const header_3 = getStepHeader("identity_verification", userRole)!;
        const prefix_1 = userRole === "seller" ? "seller" : "buyer";

        // Check if address already saved
        const addressName = contractReview?.[`${prefix_1}_billing_name` as keyof DealContractReview] as string | null;
        const hasAddress = !!addressName;
        const isWaitingForSellerVerification = userRole === "buyer" && hasAddress && deal.status === "verification";
        return <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <StepHeader {...header_3} />
          {/* {userRole === "seller" && (
            <IdentityUpload
              dealId={dealId}
              role={userRole}
              verification={verification}
              onUploadComplete={handleVerificationUpload}
            />
           )} */}

          {/* Address section */}
          {hasAddress ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-700">Vertragsadresse gespeichert</p>
                <p className="text-sm text-emerald-600">
                  {contractReview?.[`${prefix_1}_billing_company` as keyof DealContractReview] as string ? `${contractReview?.[`${prefix_1}_billing_company` as keyof DealContractReview]}, ` : ""}
                  {addressName},{" "}
                  {contractReview?.[`${prefix_1}_billing_postal_code` as keyof DealContractReview]}{" "}
                  {contractReview?.[`${prefix_1}_billing_city` as keyof DealContractReview]}
                </p>
              </div>
            </div> : <div className="space-y-2">
              <LegalEntityAddressForm role={userRole} initialValues={getAddressInitialValues(prefix_1)} onSubmit={handleUpdateBilling} />
            </div>}

          {isWaitingForSellerVerification && <div className="rounded-lg bg-blue-50 border border-blue-100 p-4 flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-800">Vertragsadresse gespeichert</p>
                <p className="text-sm text-blue-700 mt-1">
                  Wir prüfen die Ökokonto-Unterlagen des Verkäufers. Sobald die Prüfung
                  abgeschlossen ist, geht es mit dem Vertragsentwurf weiter.
                </p>
              </div>
            </div>}
        </div>;
      }
    case "eco_account_verification":
      {
        const header_2 = getStepHeader("eco_account_verification", userRole)!;

        // Buyer sees a passive waiting view
        if (userRole === "buyer") {
          return <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <StepHeader {...header_2} />
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3">
              <Clock className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-800">Prüfung läuft</p>
                <p className="text-sm text-emerald-700 mt-1">
                  Wir prüfen das Ökokonto des Verkäufers, um sicherzustellen, dass die Punkte
                  tatsächlich verfügbar sind. Sobald die Prüfung abgeschlossen ist, geht es mit dem
                  Vertragsentwurf weiter.
                </p>
              </div>
            </div>
          </div>;
        }

        // Seller sees the upload and address form
        // Check if seller address already saved
        const sellerAddressName = contractReview?.seller_billing_name as string | null;
        const hasSellerAddress = !!sellerAddressName;
        return <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <StepHeader {...header_2} />

          {/* Seller address section */}
          {hasSellerAddress ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-emerald-700">Vertragsadresse gespeichert</p>
                <p className="text-sm text-emerald-600">
                  {contractReview?.seller_billing_company ? `${contractReview.seller_billing_company}, ` : ""}
                  {sellerAddressName}, {contractReview?.seller_billing_postal_code}{" "}
                  {contractReview?.seller_billing_city}
                </p>
              </div>
            </div> : <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-on-surface-variant" />
                <h4 className="text-sm font-medium text-primary">Vertragsadresse</h4>
              </div>
              <LegalEntityAddressForm role="seller" initialValues={getAddressInitialValues("seller")} onSubmit={handleUpdateBilling} />
            </div>}

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-primary">Ökokonto-Nachweis</h4>
            <VerificationStatus verification={verification} />
            <OkokontoUpload dealId={dealId} verification={verification} offeringDocumentUrl={deal.offering?.okokonto_document_url} offeringDocumentName={deal.offering?.okokonto_document_name} onUploadComplete={handleVerificationUpload} />
          </div>
        </div>;
      }
    case "contract_preparation":
      {
        const header_1 = getStepHeader("contract_preparation", userRole, deal.status)!;

        // Active contract review: show draft + acceptance UI
        if (deal.status === "contract_review") {
          return <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <StepHeader {...header_1} />
            <ContractDraftReview dealId={dealId} role={userRole} contractReview={contractReview} onAccepted={updated_0 => {
              setContractReview(updated_0);
              onRefresh?.();
            }} />
          </div>;
        }

        // Passive waiting state (proposal_ready)
        return <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <StepHeader {...header_1} />
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-800">Vertrag wird vorbereitet</p>
              <p className="text-sm text-amber-700 mt-1">
                Unser Team bereitet den Kaufvertrag für Sie vor. Sobald der Vertrag fertig ist,
                werden Sie automatisch benachrichtigt und können ihn prüfen und unterschreiben.
              </p>
            </div>
          </div>
        </div>;
      }
    case "contract":
      {
        const header_0 = getStepHeader("contract", userRole)!;
        // What the deal trades at its agreed prices, position by position.
        const traded = tradedPositions(deal.positions ?? []);
        const subtotal = dealTotal(deal.positions ?? []);
        const provision = deal.platform_fee && subtotal ? deal.platform_fee_type === "percentage" ? subtotal * (deal.platform_fee / 100) : deal.platform_fee : null;
        return <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          <StepHeader {...header_0} />
          <ContractReviewStep dealId={dealId} role={userRole} contractReview={contractReview} onUpdateBilling={handleUpdateBilling} />

          {/* Provision breakdown for buyer */}
          {userRole === "buyer" && subtotal != null && <div className="rounded-lg border border-outline-variant/20 p-4 space-y-2">
              <h4 className="text-sm font-medium text-primary flex items-center gap-2">
                <Receipt className="w-4 h-4 text-on-surface-variant" />
                Kostenübersicht
              </h4>
              <div className="space-y-1 text-sm">
                {traded.map(position => {
                const worth = positionTotal(position);
                return worth == null ? null : <div key={position.id} className="flex justify-between">
                      <span className="text-on-surface-variant">
                        {positionQuantity(position)} × {positionPrice(position)}
                      </span>
                      <span className="text-primary">{formatCurrency(worth)}</span>
                    </div>;
              })}
                {provision != null && provision > 0 && <div className="flex justify-between">
                    <span className="text-on-surface-variant">
                      zzgl. Vermittlungsprovision
                      {deal.platform_fee_type === "percentage" ? ` (${deal.platform_fee}%)` : ""}
                    </span>
                    <span className="text-primary">{formatCurrency(provision)}</span>
                  </div>}
                <div className="flex justify-between pt-2 border-t border-outline-variant/10 font-medium">
                  <span className="text-primary">Gesamtbetrag</span>
                  <span className="text-primary">
                    {formatCurrency((subtotal ?? 0) + (provision ?? 0))}
                  </span>
                </div>
              </div>
            </div>}

          <SigningStep dealId={dealId} role={userRole} signatures={signatures} onSign={handleSign} />
        </div>;
      }
    case "unb_confirmation":
      {
        const unbStatus = deal.unb_approved_at ? "approved" : deal.unb_submitted_at ? "submitted" : "pending";
        return <UnbWaitingStatus status={unbStatus} submittedAt={deal.unb_submitted_at} approvedAt={deal.unb_approved_at} notes={deal.unb_notes} />;
      }
    case "payment_pending":
      {
        const invoiceDoc = userRole === "buyer" ? buyerDocuments.find(d_7 => d_7.type === "invoice") ?? null : null;
        const header = getStepHeader("payment_pending", userRole)!;
        return <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <StepHeader {...header} />
          </div>
          <PaymentSummary payment={payment} role={userRole} invoiceDocument={invoiceDoc} />
          <PaymentChecklist payment={payment} dealId={dealId} role={userRole} onPaymentUpdated={setPayment} />
        </div>;
      }
    case "completed":
      return <CompletionStep deal={deal as DealWithRelations} role={userRole} />;
    default:
      return null;
  }
}